import React, { useState, useEffect } from 'react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Package, Plus, Check, SlidersHorizontal } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { cn } from '../lib/cn';
import { useNotifications } from '../store/useStore';

interface Product {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  unit: string;
  base_price: number;
  is_active: boolean;
  customer_id?: string;
}

interface Customer {
  id: string;
  company_name: string;
  code_prefix?: string;
}

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductSelect?: (product: Product) => void; // Opzionale per retrocompatibilità
  priceListId?: string; // Opzionale quando mode === 'select'
  currentCustomer?: Customer | null;
  onProductsAdded?: () => void; // Callback per ricaricare i dati (modalità 'add')
  mode?: 'add' | 'select'; // 'add' aggiunge al DB, 'select' restituisce prodotti
  onProductsSelected?: (products: Product[]) => void; // Callback per restituire prodotti selezionati (modalità 'select')
  existingProductIds?: Set<string>; // Per modalità 'select', prodotti già presenti per evitare duplicati
}

const PRODUCTS_PAGE_SIZE = 60;
const NO_CATEGORY_VALUE = '__NO_CATEGORY__' as const;
type CategoryFilterValue = 'all' | typeof NO_CATEGORY_VALUE | string;

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  onProductSelect,
  priceListId,
  currentCustomer,
  onProductsAdded,
  mode = 'add',
  onProductsSelected,
  existingProductIds: externalExistingProductIds
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [debouncedCodeFilter, setDebouncedCodeFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilterValue>('all');
  // Su telefono i filtri secondari partono chiusi: altrimenti riempiono lo schermo
  // e la lista prodotti resta sotto la piega. Da `sm` in su sono sempre visibili.
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [existingProductIds, setExistingProductIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [productPage, setProductPage] = useState(0);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [totalAvailableProducts, setTotalAvailableProducts] = useState(0);
  const { addNotification } = useNotifications();

  // Gestisce il debounce della ricerca per evitare troppe chiamate a Supabase
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCodeFilter(codeFilter.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [codeFilter]);

  // Carica dati quando la modale viene aperta
  useEffect(() => {
    if (isOpen) {
      // Reset dello stato quando si apre la modale
      setSelectedProducts([]);
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setCodeFilter('');
      setDebouncedCodeFilter('');
      setSelectedCategory('all');
      setShowFilters(false);
      setProducts([]);
      setProductPage(0);
      setHasMoreProducts(true);
      setTotalAvailableProducts(0);
      setInitialDataLoaded(false);

      if (mode === 'add' && priceListId) {
        // Ricarica sempre i dati quando si apre la modale per avere dati aggiornati
        loadInitialData();
      } else if (mode === 'select') {
        // In modalità select, usa existingProductIds esterni o un Set vuoto
        if (externalExistingProductIds) {
          setExistingProductIds(externalExistingProductIds);
        }
        // Carica categorie
        loadCategories();
        setInitialDataLoaded(true);
      }
    }
  }, [isOpen, priceListId, mode]);

  const loadCategories = async () => {
    try {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('name')
        .eq('is_active', true)
        .order('name');

      if (categoriesError) {
        console.error('Errore nel caricamento categorie:', categoriesError);
        throw categoriesError;
      }

      setCategories((categoriesData || []).map(cat => cat.name).filter((name): name is string => Boolean(name)));
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
    }
  };

  const loadInitialData = async () => {
    if (!priceListId) return;

    setIsLoading(true);
    try {
      // Carica prodotti già presenti nel listino - IMPORTANTE: sempre ricaricare per avere dati aggiornati
      const { data: existingItems, error: existingError } = await supabase
        .from('price_list_items')
        .select('product_id')
        .eq('price_list_id', priceListId);

      if (existingError) {
        console.error('Errore nel caricamento prodotti esistenti:', existingError);
        throw existingError;
      }

      // Crea un nuovo Set per forzare l'aggiornamento
      const existingIds = new Set(existingItems?.map(item => item.product_id) || []);
      console.log(`Prodotti già presenti nell'offerta: ${existingIds.size}`);
      setExistingProductIds(existingIds);

      // Carica categorie attive per il filtro
      await loadCategories();

      setInitialDataLoaded(true);
    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nel caricamento dei dati'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async (page = 0, reset = false) => {
    if (reset) {
      setProducts([]);
      setProductPage(0);
      setHasMoreProducts(true);
    }

    setIsLoadingProducts(true);
    try {
      const from = page * PRODUCTS_PAGE_SIZE;
      const to = from + PRODUCTS_PAGE_SIZE - 1;

      let query = supabase
        .from('products')
        .select('id, code, name, description, category, unit, base_price, is_active, customer_id', { count: 'exact' })
        .eq('is_active', true)
        .order('name')
        .range(from, to);

      if (selectedCategory !== 'all') {
        if (selectedCategory === NO_CATEGORY_VALUE) {
          query = query.is('category', null);
        } else {
          query = query.eq('category', selectedCategory);
        }
      }

      const trimmedSearch = debouncedSearchTerm.trim();
      const trimmedCode = debouncedCodeFilter.trim();
      if (trimmedSearch) {
        const searchValue = `%${trimmedSearch}%`;
        query = query.or([
          `name.ilike.${searchValue}`,
          `code.ilike.${searchValue}`,
          `description.ilike.${searchValue}`,
          `category.ilike.${searchValue}`
        ].join(','));
      }
      if (trimmedCode) {
        query = query.ilike('code', `${trimmedCode}%`);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      const normalizedData = data || [];
      setTotalAvailableProducts(count ?? normalizedData.length);
      setHasMoreProducts(
        count !== null && count !== undefined
          ? to + 1 < count
          : normalizedData.length === PRODUCTS_PAGE_SIZE
      );
      setProducts(prev => reset ? normalizedData : [...prev, ...normalizedData]);
      setProductPage(page);
    } catch (error) {
      console.error('Errore nel caricamento prodotti:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nel caricamento dei prodotti'
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !initialDataLoaded) return;
    if (mode === 'add' && !priceListId) return;
    fetchProducts(0, true);
  }, [debouncedSearchTerm, debouncedCodeFilter, selectedCategory, currentCustomer?.id, initialDataLoaded, isOpen, priceListId, mode]);

  const handleLoadMoreProducts = () => {
    if (!hasMoreProducts || isLoadingProducts) return;
    fetchProducts(productPage + 1);
  };

  // Filtra prodotti lato client per controlli extra (cliente assegnato)
  const normalizedSearch = debouncedSearchTerm ? debouncedSearchTerm.toLowerCase() : '';
  const normalizedCodeFilter = debouncedCodeFilter ? debouncedCodeFilter.toLowerCase() : '';
  const filteredProducts = products.filter(product => {
    // Filtra i prodotti assegnati ad altri clienti
    if (currentCustomer?.id && product.customer_id && product.customer_id !== currentCustomer.id) {
      return false;
    }

    // Filtro per ricerca (ridondante rispetto al backend ma utile per sicurezza/UI)
    const matchesSearch = !normalizedSearch ||
      product.name.toLowerCase().includes(normalizedSearch) ||
      product.code.toLowerCase().includes(normalizedSearch) ||
      product.description?.toLowerCase().includes(normalizedSearch) ||
      product.category?.toLowerCase().includes(normalizedSearch);

    const matchesCode = !normalizedCodeFilter || product.code.toLowerCase().startsWith(normalizedCodeFilter);

    // Filtro per categoria
    const matchesCategory =
      selectedCategory === 'all'
        ? true
        : selectedCategory === NO_CATEGORY_VALUE
          ? !product.category
          : product.category === selectedCategory;

    return matchesSearch && matchesCode && matchesCategory;
  });
  const displayedCount = totalAvailableProducts || filteredProducts.length;
  const isInitialLoading = isLoading || (isLoadingProducts && products.length === 0);

  const handleProductToggle = (product: Product, e?: React.MouseEvent) => {
    // Previeni la propagazione dell'evento se fornito
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Non permettere la selezione se il prodotto è già presente (solo in modalità add)
    if (mode === 'add' && existingProductIds.has(product.id)) {
      return;
    }
    // In modalità select, controlla anche gli existingProductIds esterni se forniti
    if (mode === 'select' && externalExistingProductIds?.has(product.id)) {
      return;
    }

    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleAddSelectedProducts = async () => {
    if (selectedProducts.length === 0) {
      addNotification({
        type: 'warning',
        title: 'Attenzione',
        message: 'Seleziona almeno un prodotto'
      });
      return;
    }

    if (isAdding) {
      return; // Previeni click multipli
    }

    setIsAdding(true);
    try {
      if (mode === 'select') {
        // Modalità select: restituisci i prodotti selezionati al componente padre
        const productsToAdd = externalExistingProductIds
          ? selectedProducts.filter(p => !externalExistingProductIds.has(p.id))
          : selectedProducts;

        if (productsToAdd.length === 0 && externalExistingProductIds) {
          addNotification({
            type: 'warning',
            title: 'Attenzione',
            message: 'Tutti i prodotti selezionati sono già presenti'
          });
          setIsAdding(false);
          return;
        }

        onProductsSelected?.(productsToAdd);

        // Reset e chiudi
        setSelectedProducts([]);
        onClose();
      } else {
        // Modalità add: aggiungi al DB (comportamento originale)
        if (!priceListId) {
          throw new Error('priceListId è richiesto in modalità add');
        }

        // Filtra i prodotti già presenti per evitare duplicati
        const productsToAdd = selectedProducts.filter(p => !existingProductIds.has(p.id));

        if (productsToAdd.length === 0) {
          addNotification({
            type: 'warning',
            title: 'Attenzione',
            message: 'Tutti i prodotti selezionati sono già presenti nell\'offerta'
          });
          setIsAdding(false);
          return;
        }

        // Prepara i dati per l'inserimento batch
        const itemsToInsert = productsToAdd.map(product => ({
          price_list_id: priceListId,
          product_id: product.id,
          price: product.base_price,
          min_quantity: 1,
          discount_percentage: 0
        }));

        // Inserisci tutti i prodotti in una singola operazione
        const { error } = await supabase
          .from('price_list_items')
          .insert(itemsToInsert);

        if (error) {
          // Se c'è un errore di duplicato, prova ad aggiungere uno per uno
          if (error.code === '23505' || error.message.includes('duplicate')) {
            let added = 0;
            let skipped = 0;
            for (const product of productsToAdd) {
              const { error: singleError } = await supabase
                .from('price_list_items')
                .insert({
                  price_list_id: priceListId,
                  product_id: product.id,
                  price: product.base_price,
                  min_quantity: 1,
                  discount_percentage: 0
                });
              if (singleError) {
                if (singleError.code === '23505' || singleError.message.includes('duplicate')) {
                  skipped++;
                } else {
                  throw singleError;
                }
              } else {
                added++;
              }
            }
            if (added > 0) {
              addNotification({
                type: 'success',
                title: 'Prodotti aggiunti',
                message: `${added} prodotti aggiunti${skipped > 0 ? `, ${skipped} già presenti` : ''}`
              });
            }
          } else {
            throw error;
          }
        } else {
          addNotification({
            type: 'success',
            title: 'Successo',
            message: `${productsToAdd.length} prodotti aggiunti all'offerta`
          });
        }

        // Chiama il callback PRIMA di chiudere per aggiornare i dati nel componente padre
        onProductsAdded?.();

        // Reset dello stato
        setSelectedProducts([]);
        setExistingProductIds(new Set()); // Reset per forzare il ricaricamento alla prossima apertura

        // Chiudi la modale dopo un breve delay per permettere l'aggiornamento
        setTimeout(() => {
          onClose();
        }, 100);
      }
    } catch (error: any) {
      console.error('Errore nell\'aggiunta prodotti:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore nell\'aggiunta dei prodotti'
      });
    } finally {
      setIsAdding(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCodeFilter('');
    setDebouncedCodeFilter('');
    setSelectedCategory('all');
    setProductPage(0);
    setHasMoreProducts(true);
  };

  const activeFilterCount =
    (codeFilter.trim() ? 1 : 0) + (selectedCategory !== 'all' ? 1 : 0);

  const handleClose = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCodeFilter('');
    setDebouncedCodeFilter('');
    setSelectedCategory('all');
    setShowFilters(false);
    setSelectedProducts([]);
    setExistingProductIds(new Set());
    setProducts([]);
    setCategories([]);
    setInitialDataLoaded(false);
    setHasMoreProducts(true);
    setProductPage(0);
    setTotalAvailableProducts(0);
    setIsLoading(false);
    setIsLoadingProducts(false);
    setIsAdding(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="z-[90] flex flex-col gap-3 overflow-hidden sm:h-[90vh] sm:w-[90vw] sm:max-h-[90vh] sm:max-w-none"
        overlayClassName="z-[90]"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === 'select' ? 'Seleziona Prodotti' : 'Seleziona Prodotti per l\'Offerta'}
          </DialogTitle>
        </DialogHeader>

        {/* Ricerca sempre visibile, filtri secondari a scomparsa sotto `sm` */}
        <div className="flex-shrink-0 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                id="search"
                placeholder="Cerca nome, codice, categoria…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 pl-8 text-sm"
              />
            </div>
            <Button
              type="button"
              variant={activeFilterCount > 0 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters((open) => !open)}
              aria-expanded={showFilters}
              className="h-9 flex-shrink-0 px-3 text-xs sm:hidden"
            >
              <SlidersHorizontal className="mr-1 h-4 w-4" />
              Filtri{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          </div>

          <div className={cn('grid grid-cols-1 gap-2 sm:grid-cols-3', !showFilters && 'hidden sm:grid')}>
            <div>
              <Label htmlFor="codeFilter" className="text-xs font-medium text-gray-700">
                Codice Prodotto
              </Label>
              <Input
                id="codeFilter"
                placeholder="Filtra per codice (es. AB)"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value)}
                className="h-9 text-sm uppercase"
              />
            </div>

            <div>
              <Label htmlFor="category" className="text-xs font-medium text-gray-700">
                Categoria
              </Label>
              <Select value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as CategoryFilterValue)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Tutte le categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  <SelectItem value={NO_CATEGORY_VALUE}>Senza categoria</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="h-9 w-full text-xs"
              >
                Pulisci Filtri
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-600">
            {displayedCount} prodotti disponibili
            {selectedProducts.length > 0 && ` · ${selectedProducts.length} selezionati`}
          </p>
        </div>

        {/* Lista prodotti: unica area che scorre, così il dito non trova
            scroll annidati e la lista parte subito sotto la ricerca */}
        <DialogBody>
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <Package className="mx-auto mb-2 h-8 w-8 text-gray-400" />
              <p className="text-sm">
                {searchTerm || activeFilterCount > 0
                  ? 'Nessun prodotto trovato con i filtri selezionati'
                  : 'Non ci sono prodotti attivi disponibili'
                }
              </p>
              {(searchTerm || activeFilterCount > 0) && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="mt-3 text-xs">
                  Pulisci Filtri
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {filteredProducts.map(product => {
                  const isSelected = selectedProducts.some(p => p.id === product.id);
                  const isAlreadyInList = mode === 'add'
                    ? existingProductIds.has(product.id)
                    : (externalExistingProductIds?.has(product.id) ?? false);
                  return (
                    <button
                      key={product.id}
                      type="button"
                      disabled={isAlreadyInList}
                      aria-pressed={isSelected}
                      onClick={(e) => handleProductToggle(product, e)}
                      className={cn(
                        'w-full rounded-lg border p-3 text-left transition-all',
                        isAlreadyInList
                          ? 'cursor-not-allowed border-gray-300 bg-gray-100 opacity-60'
                          : isSelected
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="break-words text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-xs text-gray-500">Codice: {product.code}</div>
                          {product.category && (
                            <div className="text-xs text-gray-500">Categoria: {product.category}</div>
                          )}
                          <div className="text-xs text-gray-500">Prezzo: €{product.base_price.toFixed(2)}</div>
                          {isAlreadyInList && (
                            <div className="mt-1 text-xs font-medium text-orange-600">
                              {mode === 'add' ? 'Già nell\'offerta' : 'Già presente'}
                            </div>
                          )}
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          {isAlreadyInList ? (
                            <Check className="h-5 w-5 text-gray-400" />
                          ) : isSelected ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <div className="h-5 w-5 rounded border border-gray-300"></div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {hasMoreProducts && (
                <div className="mt-3 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadMoreProducts}
                    disabled={isLoadingProducts}
                    className="text-xs"
                  >
                    {isLoadingProducts ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                        Caricamento...
                      </>
                    ) : (
                      'Carica altri prodotti'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isAdding}>
            Annulla
          </Button>
          <Button
            onClick={handleAddSelectedProducts}
            disabled={isAdding || selectedProducts.length === 0}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            {isAdding ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Aggiunta...
              </>
            ) : (
              <>
                <Plus className="mr-1 h-4 w-4" />
                {selectedProducts.length > 0
                  ? `Aggiungi ${selectedProducts.length} prodotti`
                  : 'Aggiungi prodotti'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductSelectionModal;
